import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enUS, es } from "date-fns/locale";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { styled, useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import FeaturedVideoOutlinedIcon from "@mui/icons-material/FeaturedVideoOutlined";
import { connect } from "react-redux";
import { BarLoader } from "@/_components";
import moment from "moment-timezone";
import ContinueButton from "./ContinueButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import Cookies from "universal-cookie";
import { USA } from "@/_helpers/CountryConstants";
import { LifeSafer } from "../../_helpers/BrandConstants";
import { loadStates } from "../../_redux/actions/userActions";
import { utilities } from "../../_helpers/utilities";
import { useParams } from "react-router-dom";
// import { useDeviceTypeWatcher } from "@/_helpers/PhoneNumberHelper";

const FormRoot = styled("form")(({ theme }) => ({
  width: "100%",
  marginTop: theme.spacing(1),
  marginBottom: "40px",
  display: "flex",
  justifyContent: "center",
  maxWidth: "900px",
}));

const InputIcon = styled(FeaturedVideoOutlinedIcon)({
  opacity: 0.6,
  color: "#1c355e",
});

const StateId = ({
  submitting,
  loading,
  handleUpdate,
  statesList,
  goToBackStep,
  loadStates,
}) => {
  const countryCode = sessionStorage.getItem("LSCountryCode");
  const enrolleeUser = JSON.parse(sessionStorage.getItem("enrolleeUser"));
  const theme = useTheme();
  // const locale = countryCode === "USA" ? enUS : enAU;
  const { t, i18n } = useTranslation();
  // const locale = countryCode === "USA" ? enUS : es;
  const locale = i18n.language === "es" ? es : enUS;
  const isBelow998 = useMediaQuery(theme.breakpoints.down(998));
  const cookies = new Cookies();
  const siteCountryId = cookies.get("siteCountry") || USA;
  const brand = cookies.get("lsEnrollmentReferral") || LifeSafer;
  const { storyName } = useParams();
  const isSpeedStory =
    storyName?.toLowerCase() === "speed" ||
    storyName?.toLowerCase() === "isa volunteer" ||
    enrolleeUser?.story?.toLowerCase() === "isa volunteer";
  const referenceTimezone =
    countryCode === "AU" ? "Australia/Sydney" : "America/New_York";
  // const { isISA } = useDeviceTypeWatcher();

  const isSpeed =
    storyName?.toLowerCase() === "speed" ||
    storyName?.toLowerCase() === "isa volunteer";
  // const [isDobInteracted, setIsDobInteracted] = useState(false);

  const driverAgeOptions = [
    { label: "16", value: 16 },
    { label: "17", value: 17 },
    { label: "18", value: 18 },
    { label: "19", value: 19 },
    { label: "20", value: 20 },
    { label: "21+", value: 21 },
  ];

  const getDriverAgeFromDob = (dob) => {
    // if (!dob) return null;
    // if (typeof dob === "string" && dob.startsWith("01/01/0001")) return null;

    const parsedDob = new Date(dob);
    if (Number.isNaN(parsedDob.getTime())) return null;

    const today = new Date();
    let age = today.getFullYear() - parsedDob.getFullYear();
    const monthDiff = today.getMonth() - parsedDob.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < parsedDob.getDate())
    ) {
      age -= 1;
    }

    if (age >= 21) return driverAgeOptions.find((x) => x.value === 21);
    if (age < 16 || age > 20) return null;

    return driverAgeOptions.find((x) => x.value === age);
  };

  // const isPlaceholderDob = (dob) => {
  //   const raw = String(dob || "").trim();
  //   return (
  //     raw.includes("0001") ||
  //     raw.startsWith("01/01/2001") ||
  //     raw.startsWith("2001-01-01")
  //   );
  // };

  // const normalizeDob = (dob) => {
  //   // if (!dob || isPlaceholderDob(dob)) return null;
  //   // if (!dob) return null;
  //   const parsed = dob instanceof Date ? dob : new Date(dob);
  //   // if (Number.isNaN(parsed.getTime())) return null;
  //   return parsed;
  // };
  const normalizeDob = (dob) => {
    if (!dob) return null;

    const parsed = dob instanceof Date ? dob : new Date(dob);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      idState: null,
      dlNum: utilities.normalizeApiFieldValue(enrolleeUser?.dlNum) ?? null,
      dob: normalizeDob(enrolleeUser?.dob),
      driverAge: getDriverAgeFromDob(enrolleeUser?.dob),
    },
  });

  useEffect(() => {
    if (
      statesList === undefined ||
      statesList[0].CurrentState === "Undefined"
    ) {
      loadStates(siteCountryId, brand).catch((error) => {
        console.log("Loading violation dashboard failed! " + error);
      });
    }
  }, []);

  const onSubmit = (data) => {
    const selectedAge = data?.driverAge?.value;
    const ageBasedDob = (() => {
      if (!selectedAge) return null;
      return moment
        .tz(referenceTimezone)
        .startOf("day")
        .subtract(selectedAge, "years")
        .toDate();
    })();

    const dobDate = normalizeDob(isSpeed ? ageBasedDob : data?.dob);

    const updatedState = {
      dlState: data.idState.shortName || null,
      idState: data.idState.shortName || "CE - NEED",
      dlNum: data.dlNum || "CE - NEED",
      dob: dobDate,
    };

    let oldData = {
      EnrolleeId: enrolleeUser?.enrolleeId,
      ClientId: enrolleeUser?.clientId,
      UserId: enrolleeUser?.userId,
      firstName: enrolleeUser?.firstName,
      lastName: enrolleeUser?.lastName,
      country: enrolleeUser?.country,
      dlExpirationDate: enrolleeUser?.dlExpirationDate,
      email: enrolleeUser?.email,
      homePhone: enrolleeUser?.homePhone,
      cellPhone: enrolleeUser?.cellPhone,
      workPhone: enrolleeUser?.workPhone,
      programType: enrolleeUser?.programType,
      protPlan: enrolleeUser?.protPlan,
      contractEnd: enrolleeUser?.contractEnd,
      deviceType:
        enrolleeUser?.clientTypeId === 1
          ? "Interlock"
          : enrolleeUser?.clientTypeId === 5
            ? "Portable"
            : enrolleeUser?.clientTypeId === 6
              ? "Speed"
              : "Interlock",
    };

    const formattedDate = dobDate ? moment(dobDate).format("MM/DD/YYYY") : null;

    const updatedData = {
      ...updatedState,
      ...oldData,
      dob: formattedDate,
      step: "StateId",
      enrollmentCode:
        enrolleeUser?.enrollmentCode || enrolleeUser?.EnrollmentCode,
      enrollmentCodeId:
        enrolleeUser?.enrollmentCodeId ||
        enrolleeUser?.EnrollmentCodeId ||
        enrolleeUser?.EnrollmentCodeID,
      deviceType: enrolleeUser?.deviceType,
    };

    const safeenrolleeUser =
      typeof enrolleeUser === "object" && enrolleeUser !== null
        ? enrolleeUser
        : {};

    handleUpdate("StateId", {
      ...safeenrolleeUser,
      ...updatedData,
    });
  };

  const selectedState =
    statesList?.find((s) => s.shortName === enrolleeUser?.dlState) || "";

  useEffect(() => {
    if (!statesList?.length) return;

    if (enrolleeUser?.country === "NZL" || enrolleeUser?.country === "KOR") {
      setValue("idState", statesList[0]);
    } else {
      setValue("idState", selectedState);
    }

    setValue(
      "dlNum",
      utilities.normalizeApiFieldValue(enrolleeUser?.dlNum) ?? null,
    );
    if (isSpeedStory) {
      setValue("driverAge", getDriverAgeFromDob(enrolleeUser?.dob));
    } else {
      setValue("dob", normalizeDob(enrolleeUser?.dob));
    }
  }, [statesList, selectedState, setValue]);

  if (loading) {
    return (
      <Grid sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <BarLoader />
      </Grid>
    );
  }

  return (
    <FormRoot onSubmit={handleSubmit(onSubmit)}>
      <Grid
        container
        alignItems="start"
        justifyContent="center"
        sx={{
          width: "100%",
          marginTop: "25px",
        }}
        spacing="25px"
      >
        <Grid style={{ maxWidth: "300px", width: "100%" }}>
          <Controller
            name="idState"
            control={control}
            rules={{
              required: {
                value: true,
                message: t("location.stateRequired"),
              },
            }}
            // rules={{
            //   required: { value: true, message: t("location.stateRequired") },
            // }}
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={statesList}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.shortName === value?.shortName
                }
                onChange={(_, data) => field.onChange(data)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("location.state")}
                    placeholder={t("location.chooseState")}
                    disabled={submitting}
                    error={!!errors.idState}
                    helperText={errors.idState?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid style={{ maxWidth: "300px", width: "100%" }}>
          {isSpeed ? (
            <Controller
              name="driverAge"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("body.driverAgeRequired"),
                },
              }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={driverAgeOptions}
                  disableClearable
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.value === value?.value
                  }
                  onChange={(_, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label={t("body.driverAgeLabel")}
                      placeholder={t("body.driverAgePlaceholder")}
                      disabled={submitting}
                      error={!!errors.driverAge}
                      helperText={errors.driverAge?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              )}
            />
          ) : (
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={locale}
            >
              <Controller
                name="dob"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t("body.dobRequired"),
                  },
                  validate: (value) => {
                    if (!value) return t("body.dobRequired");

                    const dob = moment(value);
                    const minDate = moment().subtract(100, "years");
                    const maxDate = moment().subtract(14, "years");

                    if (!dob.isValid()) {
                      return t("body.dobInvalid");
                    }

                    if (dob.isBefore(minDate) || dob.isAfter(maxDate)) {
                      return t("body.dobInvalid");
                    }

                    return true;
                  },
                }}
                render={({ field }) => (
                  <DesktopDatePicker
                    {...field}
                    value={normalizeDob(field.value)}
                    onChange={(value) => {
                      // setIsDobInteracted(true);
                      field.onChange(normalizeDob(value));
                    }}
                    label={t("body.dobLabel")}
                    format={countryCode === "AU" ? "dd/MM/yyyy" : "MM/dd/yyyy"}
                    disableFuture
                    openTo="year"
                    views={["year", "month", "day"]}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        placeholder: "MM/DD/YYYY",
                        error: !!errors.dob,
                        disabled: submitting,
                        helperText: errors.dob?.message,
                        InputLabelProps: { shrink: true },
                        // onInput: () => setIsDobInteracted(true),
                        // sx: {
                        //   "& .MuiPickersInputBase-sectionsContainer": {
                        //     opacity: field.value || isDobInteracted ? 1 : 1,
                        //   },
                        // },
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          )}
        </Grid>
        <Grid style={{ maxWidth: "300px", width: "100%" }}>
          <TextField
            {...register("dlNum", {
              required: {
                value: true,
                message: t("body.IDNumberRequired"),
              },
            })}
            onInput={(e) => {
              e.target.value = e.target.value.toUpperCase();
            }}
            fullWidth
            size="small"
            label={t("body.IDNumberLabel")}
            placeholder={t("body.IDNumberLabel")}
            disabled={submitting}
            error={!!errors.dlNum}
            helperText={errors.dlNum?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <InputIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid
          sx={{ maxWidth: isBelow998 ? "300px" : "900px", paddingX: "10px" }}
        >
          <Typography sx={{ fontSize: 14, color: "#707070" }}>
            {t("location.haveAnId")}
          </Typography>
        </Grid>

        <Grid
          container
          alignItems="flex-end"
          justifyContent="flex-end"
          direction="column"
          sx={{ mt: 2 }}
        >
          <ContinueButton goToBackStep={goToBackStep} submitting={submitting} />
        </Grid>
      </Grid>
    </FormRoot>
  );
};

StateId.propTypes = {
  submitting: PropTypes.bool,
  loading: PropTypes.bool,
  handleUpdate: PropTypes.func,
  statesList: PropTypes.array,
  goToBackStep: PropTypes.func,
  loadStates: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    statesList: state.statesList,
  };
}

const mapDispatchToProps = {
  loadStates,
};

export default connect(mapStateToProps, mapDispatchToProps)(StateId);
